/**
 * Templates — whole compositions built from a list of media and a brand.
 *
 * `buildCompositionFromStoryboard` is a stable, exported API: the AI
 * storyboard feature calls it with the scenes it planned. Change its input
 * shape only additively.
 */
import {
    AspectRatio,
    ClipTransition,
    MainClip,
    OPENER,
    MotionType,
    OverlayLayer,
    TransitionType,
    TRANSITION_DEFAULT_SECONDS,
    TRANSITION_TYPES,
    VideoComposition,
    addAudioClip,
    addOverlayLayer,
    createAudioClip,
    createEmptyComposition,
    createMainClip,
    insertMainClips,
    itemEnd,
    mainTrack,
    normalizeComposition,
} from './composition';
import { TextPresetId, TEXT_PRESETS, VideoBrand, buildPresetLayers, buildScrim } from './presets';

export interface TemplateMedia {
    src: string;
    kind: 'video' | 'image';
    assetUuid?: string;
    thumbnailUrl?: string;
    /** Length of a video source, when known. */
    durationSec?: number;
    /** Shown by templates that caption each shot (product name). */
    title?: string;
    /** A second caption line (size, finish). */
    subtitle?: string;
}

export interface TemplateOptions {
    aspect?: AspectRatio;
    fps?: number;
    brand?: VideoBrand;
    /** Opening line. Falls back to the brand name. */
    headline?: string;
    /** Small line above the headline. */
    eyebrow?: string;
    /** Labels the before/after template writes on screen (translated by the caller). */
    labels?: { before?: string; after?: string };
    /** A music bed laid under the whole video. */
    music?: { src: string; name?: string; durationSec?: number; volume?: number };
    /** Append a logo end card (default: when the brand has a logo or a name). */
    endCard?: boolean;
}

export type TemplateId = 'texture-reel' | 'slow-showcase' | 'collection-lineup' | 'before-after';

export interface TemplateMeta {
    id: TemplateId;
    nameKey: string;
    descriptionKey: string;
    /** Fewest media the template makes sense with. */
    minMedia: number;
}

export const TEMPLATES: TemplateMeta[] = [
    { id: 'slow-showcase', nameKey: 'VIDEO_EDITOR.TEMPLATE.SLOW_SHOWCASE', descriptionKey: 'VIDEO_EDITOR.TEMPLATE.SLOW_SHOWCASE_DESC', minMedia: 1 },
    { id: 'texture-reel', nameKey: 'VIDEO_EDITOR.TEMPLATE.TEXTURE_REEL', descriptionKey: 'VIDEO_EDITOR.TEMPLATE.TEXTURE_REEL_DESC', minMedia: 1 },
    { id: 'collection-lineup', nameKey: 'VIDEO_EDITOR.TEMPLATE.COLLECTION_LINEUP', descriptionKey: 'VIDEO_EDITOR.TEMPLATE.COLLECTION_LINEUP_DESC', minMedia: 1 },
    { id: 'before-after', nameKey: 'VIDEO_EDITOR.TEMPLATE.BEFORE_AFTER', descriptionKey: 'VIDEO_EDITOR.TEMPLATE.BEFORE_AFTER_DESC', minMedia: 2 },
];

/** Long enough that the logo and tagline are fully readable for over two seconds. */
const END_CARD_SECONDS = 3.0;

function hasEndCard(opts: TemplateOptions): boolean {
    if (opts.endCard !== undefined) return opts.endCard;
    return !!(opts.brand?.logoUrl || opts.brand?.brandName);
}

/** A video clip cut from the middle of its source (where the motion usually is). */
function cutFrom(m: TemplateMedia, duration: number, extra: Partial<MainClip> = {}, align: 'start' | 'middle' = 'middle'): MainClip {
    const src = m.durationSec;
    let trimIn = 0;
    if (m.kind === 'video' && src && src > duration && align === 'middle') trimIn = (src - duration) / 2;
    const clip = createMainClip({
        kind: m.kind,
        src: m.src,
        assetUuid: m.assetUuid,
        thumbnailUrl: m.thumbnailUrl,
        duration,
        sourceDuration: src,
        trimIn,
        label: m.title,
    });
    return { ...clip, ...extra };
}

const KEN_BURNS: MotionType[] = ['zoom-in', 'pan-left', 'zoom-out', 'pan-right', 'pan-up'];

function withEndCard(comp: VideoComposition, opts: TemplateOptions, transition: ClipTransition | undefined): VideoComposition {
    if (!hasEndCard(opts)) return comp;
    const start = itemEnd(mainTrack(comp).clips.slice(-1)[0] || { start: 0, duration: 0 }) - (transition?.duration ?? 0);
    const layers = buildPresetLayers('end-card', {
        aspect: comp.aspect,
        start: Math.max(0, start),
        duration: END_CARD_SECONDS + (transition?.duration ?? 0),
        brand: opts.brand,
        texts: { primary: opts.brand?.tagline || '', secondary: opts.brand?.website || '' },
    });
    // The ground fades in over the last clip, which is the transition.
    const fadeIn = transition?.duration ?? 0.6;
    return layers.reduce((acc, l, i) => addOverlayLayer(acc, i === 0 ? { ...l, animDuration: fadeIn } : l), comp);
}

function withMusic(comp: VideoComposition, opts: TemplateOptions, total: number): VideoComposition {
    if (!opts.music?.src) return comp;
    const duration = opts.music.durationSec ? Math.min(opts.music.durationSec, total) : total;
    return addAudioClip(comp, createAudioClip({
        src: opts.music.src,
        name: opts.music.name,
        duration,
        sourceDuration: opts.music.durationSec,
        volume: opts.music.volume ?? 0.75,
    }));
}

function totalWithEndCard(comp: VideoComposition, opts: TemplateOptions): number {
    const clips = mainTrack(comp).clips;
    const mainEnd = clips.length ? itemEnd(clips[clips.length - 1]) : 0;
    return mainEnd + (hasEndCard(opts) ? END_CARD_SECONDS : 0);
}

function addLayers(comp: VideoComposition, layers: OverlayLayer[]): VideoComposition {
    return layers.reduce((acc, l) => addOverlayLayer(acc, l), comp);
}

// ─── templates ────────────────────────────────────────────────────────

/** Three to five long, slow shots; crossfades; an editorial headline. */
function slowShowcase(media: TemplateMedia[], opts: TemplateOptions): VideoComposition {
    let comp = base(opts);
    const shots = media.slice(0, 5);
    const each = shots.length <= 2 ? 4.8 : shots.length === 3 ? 4.2 : 3.6;
    const clips = shots.map((m, i) => cutFrom(m, each, {
        motion: { type: KEN_BURNS[i % KEN_BURNS.length], intensity: 0.45 },
        transitionIn: i === 0 ? OPENER : { type: 'crossfade', duration: 0.9 },
    }));
    comp = insertMainClips(comp, clips);
    const laid = mainTrack(comp).clips;
    const first = laid[0];
    const headline = opts.headline || opts.brand?.brandName;
    if (first && headline) {
        comp = addLayers(comp, [
            buildScrim(comp.aspect, first.start, first.duration, 'bottom'),
            ...buildPresetLayers('eyebrow-headline', {
                aspect: comp.aspect,
                start: first.start + 0.5,
                duration: Math.max(1.5, first.duration - 0.6),
                brand: opts.brand,
                texts: { primary: headline, secondary: opts.eyebrow || '' },
            }).filter((l) => l.kind !== 'text' || (l as any).text),
        ]);
    }
    // A quiet caption on the middle shot, when a product name is known.
    const mid = laid[Math.floor(laid.length / 2)];
    const midMedia = shots[Math.floor(laid.length / 2)];
    if (laid.length >= 3 && mid && midMedia?.title) {
        comp = addLayers(comp, buildPresetLayers('minimal-caption', {
            aspect: comp.aspect,
            start: mid.start + 0.6,
            duration: Math.max(1.2, mid.duration - 1.2),
            texts: { primary: midMedia.title },
        }));
    }
    comp = withEndCard(comp, opts, { type: 'crossfade', duration: 0.9 });
    return withMusic(comp, opts, totalWithEndCard(comp, opts));
}

/** Fast macro cuts (~1.1 s), two or three caption beats, logo end card. */
function textureReel(media: TemplateMedia[], opts: TemplateOptions): VideoComposition {
    let comp = base(opts);
    // A reel needs rhythm: at least eight cuts, re-using shots with a different crop of time.
    const minCuts = 8;
    const shots: TemplateMedia[] = [];
    for (let i = 0; shots.length < Math.max(minCuts, media.length) && media.length; i++) shots.push(media[i % media.length]);
    const clips = shots.slice(0, 12).map((m, i) => {
        const pass = Math.floor(i / Math.max(1, media.length));
        const clip = cutFrom(m, 1.1, {
            motion: { type: i % 2 ? 'zoom-out' : 'zoom-in', intensity: 0.85 },
            transitionIn: i === 0
                ? OPENER
                : i % 4 === 0 ? { type: 'zoom', duration: 0.3 } : undefined,
        }, 'start');
        // Later passes over the same video start further in, so a repeat is a new shot.
        if (m.kind === 'video' && m.durationSec && m.durationSec > 1.1) {
            const room = m.durationSec - 1.1;
            clip.trimIn = Math.min(room, ((pass * 0.37 + (i % 3) * 0.21) % 1) * room);
        }
        return clip;
    });
    comp = insertMainClips(comp, clips);
    const laid = mainTrack(comp).clips;
    const beats: Array<{ at: number; preset: TextPresetId; primary: string; secondary?: string }> = [];
    const opener = opts.headline || opts.brand?.brandName;
    if (opener) beats.push({ at: 0, preset: 'tracked-title', primary: opener });
    const mid = Math.floor(laid.length / 2);
    const withSpec = media.find((m) => m.subtitle);
    if (withSpec?.subtitle) beats.push({ at: mid, preset: 'spec-tag', primary: withSpec.subtitle });
    else if (media[0]?.title) beats.push({ at: mid, preset: 'spec-tag', primary: media[0].title });
    for (const b of beats) {
        const from = laid[b.at];
        if (!from) continue;
        const to = laid[Math.min(laid.length - 1, b.at + 1)];
        comp = addLayers(comp, buildPresetLayers(b.preset, {
            aspect: comp.aspect,
            start: from.start + 0.1,
            duration: itemEnd(to) - from.start - 0.2,
            brand: opts.brand,
            texts: { primary: b.primary, secondary: b.secondary },
        }));
    }
    comp = withEndCard(comp, opts, { type: 'fade-black', duration: 0.4 });
    return withMusic(comp, opts, totalWithEndCard(comp, opts));
}

/** One product per shot with its name and size, sliding along. */
function collectionLineup(media: TemplateMedia[], opts: TemplateOptions): VideoComposition {
    let comp = base(opts);
    const clips = media.slice(0, 10).map((m, i) => cutFrom(m, 2.4, {
        motion: { type: 'zoom-in', intensity: 0.3 },
        transitionIn: i === 0 ? OPENER : { type: 'slide-left', duration: 0.5 },
    }));
    comp = insertMainClips(comp, clips);
    const laid = mainTrack(comp).clips;
    const opener = opts.headline || opts.brand?.brandName;
    laid.forEach((clip, i) => {
        const m = media[i];
        if (i === 0 && opener) {
            comp = addLayers(comp, buildPresetLayers('editorial-serif', {
                aspect: comp.aspect,
                start: clip.start + 0.3,
                duration: clip.duration - 0.7,
                brand: opts.brand,
                texts: { primary: opener },
            }));
            return;
        }
        if (!m?.title && !m?.subtitle) return;
        comp = addLayers(comp, buildPresetLayers('lower-third', {
            aspect: comp.aspect,
            start: clip.start + 0.45,
            duration: clip.duration - 0.8,
            brand: opts.brand,
            texts: { primary: m.title || m.subtitle || '', secondary: m.title ? m.subtitle : undefined },
        }));
    });
    comp = withEndCard(comp, opts, { type: 'crossfade', duration: 0.6 });
    return withMusic(comp, opts, totalWithEndCard(comp, opts));
}

/** Two shots, a slow wipe between them, labelled. */
function beforeAfter(media: TemplateMedia[], opts: TemplateOptions): VideoComposition {
    let comp = base(opts);
    const [a, b] = media;
    if (!a) return comp;
    const clips = [
        cutFrom(a, 2.6, { motion: { type: 'zoom-in', intensity: 0.2 }, transitionIn: OPENER }),
    ];
    if (b) clips.push(cutFrom(b, 3.4, { motion: { type: 'zoom-in', intensity: 0.2 }, transitionIn: { type: 'wipe', duration: 1.1 } }));
    comp = insertMainClips(comp, clips);
    const laid = mainTrack(comp).clips;
    const before = opts.labels?.before || 'Before';
    const after = opts.labels?.after || 'After';
    comp = addLayers(comp, buildPresetLayers('spec-tag', {
        aspect: comp.aspect, start: laid[0].start + 0.3, duration: laid[0].duration - 0.6, texts: { primary: before },
    }).map((l) => ({ ...l, y: 0.12 })));
    if (laid[1]) {
        comp = addLayers(comp, buildPresetLayers('spec-tag', {
            aspect: comp.aspect, start: laid[1].start + 0.9, duration: laid[1].duration - 1.0, texts: { primary: after },
        }).map((l) => ({ ...l, y: 0.12 })));
    }
    comp = withEndCard(comp, opts, { type: 'crossfade', duration: 0.7 });
    return withMusic(comp, opts, totalWithEndCard(comp, opts));
}

function base(opts: TemplateOptions): VideoComposition {
    const comp = createEmptyComposition(opts.aspect || '9:16', opts.fps || 30);
    return { ...comp, background: '#0E0E0C' };
}

const BUILDERS: Record<TemplateId, (media: TemplateMedia[], opts: TemplateOptions) => VideoComposition> = {
    'texture-reel': textureReel,
    'slow-showcase': slowShowcase,
    'collection-lineup': collectionLineup,
    'before-after': beforeAfter,
};

/** Build a full composition from media using a named template. */
export function applyTemplate(id: TemplateId, media: TemplateMedia[], opts: TemplateOptions = {}): VideoComposition {
    const build = BUILDERS[id] || slowShowcase;
    return normalizeComposition(build(media.filter((m) => !!m?.src), opts));
}

// ─── storyboard (stable API) ──────────────────────────────────────────

export interface StoryboardScene {
    /** Media URL; may be empty for a 'color' scene. */
    src: string;
    /** 'color' is a plain card (title / end frame) filled with `color`. */
    kind: 'video' | 'image' | 'color';
    assetUuid?: string;
    durationSec: number;
    caption?: { text: string; preset?: string };
    /**
     * How this scene arrives. A TransitionType, or a loose word an LLM might
     * use: 'cut' | 'none' | 'dissolve' | 'fade' | 'dip-to-black' | 'dip-to-white'
     * | 'slide' | 'push' | 'zoom' | 'blur' | 'wipe' | 'iris' | 'circle'
     * | 'flash' | 'white-flash' | 'whip' | 'whip-pan'.
     */
    transition?: string;
    /** Poster for the timeline and the canvas card. */
    thumbnailUrl?: string;
    /** Video only: seconds into the source the clip starts (skip a model's settling frames). */
    trimInSec?: number;
    /** Video only: the source's length, so trimming stays inside it. */
    sourceDurationSec?: number;
    /** Video only: playback rate (0.25–2; 0.5 = slow motion). */
    speed?: number;
    /** 'color' scenes only: the card's fill, '#RRGGBB'. */
    color?: string;
}

export interface StoryboardOptions {
    aspect?: AspectRatio;
    fps?: number;
    brand?: VideoBrand;
    /** Defaults to true when the brand has a logo or a name. */
    endCard?: boolean;
    music?: { src: string; name?: string; durationSec?: number; volume?: number };
    /** Preset for captions that do not name one. */
    captionPreset?: TextPresetId;
    /** Transition for scenes that do not name one (default crossfade). */
    defaultTransition?: TransitionType | 'cut';
    /** Ken Burns on still images (default true). */
    kenBurns?: boolean;
}

const TRANSITION_ALIASES: Record<string, TransitionType | 'cut'> = {
    cut: 'cut',
    none: 'cut',
    hard: 'cut',
    dissolve: 'crossfade',
    fade: 'crossfade',
    'cross-fade': 'crossfade',
    crossfade: 'crossfade',
    'dip-to-black': 'fade-black',
    'fade-to-black': 'fade-black',
    black: 'fade-black',
    'dip-to-white': 'fade-white',
    'fade-to-white': 'fade-white',
    white: 'fade-white',
    slide: 'slide-left',
    push: 'slide-left',
    'slide-right': 'slide-left',
    'slide-down': 'slide-up',
    zoom: 'zoom',
    'zoom-in': 'zoom',
    blur: 'blur',
    wipe: 'wipe',
    circle: 'iris',
    'iris-in': 'iris',
    'circle-reveal': 'iris',
    'white-flash': 'flash',
    'flash-cut': 'flash',
    'whip-pan': 'whip',
    swish: 'whip',
};

export function resolveTransition(word: string | undefined, fallback: TransitionType | 'cut' = 'crossfade'): TransitionType | 'cut' {
    if (!word) return fallback;
    const key = word.trim().toLowerCase();
    if ((TRANSITION_TYPES as string[]).includes(key)) return key as TransitionType;
    return TRANSITION_ALIASES[key] ?? fallback;
}

const TRANSITION_SECONDS = TRANSITION_DEFAULT_SECONDS;

function isPresetId(v: string | undefined): v is TextPresetId {
    return !!v && TEXT_PRESETS.some((p) => p.id === v);
}

/**
 * Turn a planned storyboard into an editable composition.
 *
 * Each scene becomes one main clip of `durationSec`; a caption becomes a text
 * layer over that scene (inset so it lands after the transition and leaves
 * before the next). Stills get a gentle Ken Burns. A logo end card and a music
 * bed are added when the options supply them.
 */
export function buildCompositionFromStoryboard(scenes: StoryboardScene[], opts: StoryboardOptions = {}): VideoComposition {
    let comp = base({ aspect: opts.aspect, fps: opts.fps });
    const kenBurns = opts.kenBurns !== false;
    const valid = (scenes || []).filter((s) => s && (s.src || (s.kind === 'color' && s.color)));
    const transitions = valid.map((s, i) => {
        // The opener is always the picture itself (the first frame is the
        // cover): whatever the board says about scene 1, it soft-opens.
        if (i === 0) return OPENER;
        const word = resolveTransition(s.transition, opts.defaultTransition ?? 'crossfade');
        return word === 'cut' ? undefined : { type: word, duration: TRANSITION_SECONDS[word] };
    });
    const clips = valid.map((s, i) => {
        const transitionIn = transitions[i];
        // A scene holds on screen for its own durationSec: the next scene's
        // transition overlaps it, so it runs that much longer. Otherwise every
        // crossfade shortens the video and a 15 s board comes out at 12.
        const overlapOut = i + 1 < valid.length ? (transitions[i + 1]?.duration ?? 0) : 0;
        const kind = s.kind === 'video' || s.kind === 'color' ? s.kind : 'image';
        const clip = createMainClip({
            kind,
            src: s.src || '',
            color: kind === 'color' ? s.color : undefined,
            assetUuid: s.assetUuid,
            thumbnailUrl: s.thumbnailUrl,
            duration: Math.max(0.4, Number(s.durationSec) || 3) + overlapOut,
            trimIn: kind === 'video' ? Math.max(0, Number(s.trimInSec) || 0) : 0,
            sourceDuration: kind === 'video' && s.sourceDurationSec ? s.sourceDurationSec : undefined,
            speed: kind === 'video' && s.speed ? s.speed : undefined,
            transitionIn,
            motion: s.kind === 'image'
                ? (kenBurns ? { type: KEN_BURNS[i % KEN_BURNS.length], intensity: 0.4 } : { type: 'none', intensity: 0 })
                : undefined,
        });
        return clip;
    });
    comp = insertMainClips(comp, clips);
    const laid = mainTrack(comp).clips;
    let needsScrim = false;
    valid.forEach((s, i) => {
        const clip = laid[i];
        if (!clip || !s.caption?.text?.trim()) return;
        const preset: TextPresetId = isPresetId(s.caption.preset) ? s.caption.preset : (opts.captionPreset || 'editorial-serif');
        const next = laid[i + 1];
        const leadIn = Math.min(0.5, (clip.transitionIn?.duration ?? 0) + 0.15);
        const tail = next?.transitionIn ? next.transitionIn.duration : 0.15;
        const duration = Math.max(0.6, clip.duration - leadIn - tail);
        const [primary, secondary] = s.caption.text.split('\n');
        if (preset !== 'spec-tag' && preset !== 'end-card') needsScrim = true;
        const layers = buildPresetLayers(preset, {
            aspect: comp.aspect,
            start: clip.start + leadIn,
            duration,
            brand: opts.brand,
            texts: { primary: primary.trim(), secondary: secondary?.trim() },
        });
        // The opener's line is the headline of the whole video: a size up.
        comp = addLayers(comp, i === 0
            ? layers.map((l) => (l.kind === 'text' && preset !== 'spec-tag' ? { ...l, fontSize: l.fontSize * 1.15 } : l))
            : layers);
    });
    if (needsScrim && laid.length) {
        // One soft scrim under the captions; the bottom of the frame only.
        const end = itemEnd(laid[laid.length - 1]);
        comp = addOverlayLayer(comp, { ...buildScrim(comp.aspect, 0, end, 'bottom'), opacity: 0.4 });
        // The scrim must sit beneath the captions: move its track first.
        comp = sinkScrims(comp);
    }
    const endOpts: TemplateOptions = { brand: opts.brand, endCard: opts.endCard, music: opts.music };
    comp = withEndCard(comp, endOpts, { type: 'crossfade', duration: 0.8 });
    comp = withMusic(comp, endOpts, totalWithEndCard(comp, endOpts));
    return normalizeComposition(comp);
}

/** Put overlay tracks that only hold scrims directly above the main track. */
function sinkScrims(comp: VideoComposition): VideoComposition {
    const isScrimTrack = (t: any) => t.kind === 'overlay' && t.layers.length > 0 && t.layers.every((l: any) => l.kind === 'shape' && l.shape === 'scrim');
    const main = comp.tracks.filter((t) => t.kind === 'main');
    const scrims = comp.tracks.filter(isScrimTrack);
    const others = comp.tracks.filter((t) => t.kind !== 'main' && !isScrimTrack(t));
    return { ...comp, tracks: [...main, ...scrims, ...others] };
}
